import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { appStatus } from "../../store/modules/app_status/actions";
import {
  BackgroundPattern,
  ButtonRow,
  ButtonContainerLeft,
  ButtonContainerRight,
  StyledButton as AreasStyledButton,
} from "../../components/guardian/ListAreas/styles";
import {
  MainContainer,
  SideContainer,
  PageContent,
  StyledButtonSpan,
  StyledSpan,
  ActionsCell,
  ActionButton,
  Header,
  Title,
  EmptyMessage,
  StyledTable as Table,
  StyledTableHead as THead,
  StyledTableRow as TR,
  StyledTableHeader as TH,
  StyledTableCell as TD,
  TableContainer,
  DialogOverlay,
  DialogPanel,
  FeedbackPanel,
  DialogTitle,
  DialogText,
  DialogFooter,
  DialogButton,
  FeedbackIconWrap,
  DesktopOnly,
  MobileCards,
  MobileCard,
  MobileCardTitle,
  MobileCardMeta,
  MobileCardActions,
} from "./styles";
import { guardianAlertService } from "../../services/guardianAlertService";
import Sidebar from "../../components/sidebar";
import RequestModal from "../../components/RequestModal";
import { useFullscreenState } from "../../utils/fullscreen";
import AlertRequestCodeSearchBar from "../../components/guardian/ViewAlertRequest/AlertRequestCodeSearchBar";
import { normalizeAlertCode } from "../../utils/guardianAlertByCode";
import { canCreateGuardianAlert } from "../../utils/userRoles";
import { openAlertFromAlertRequest } from "../../utils/openAlertFromAlertRequest";
import AlertRequestResolutionBadge from "../../components/guardian/AlertRequestResolutionBadge";
import Swal from "sweetalert2";

const REJECT_SUCCESS_MESSAGE =
  "A solicitação foi rejeitada com sucesso.";

const GENERIC_ERROR_MESSAGE =
  "Não foi possível concluir a operação. Tente novamente em instantes.";

export default function ViewAlertRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);
  const [rejectModalRequest, setRejectModalRequest] = useState(null);
  const [detailRequest, setDetailRequest] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState(null);
  const [openingAlertId, setOpeningAlertId] = useState(null);
  const { fullscreenActive, toggleFullscreen } = useFullscreenState();
  const location = useLocation();
  const collapsed = useSelector((state) => state.sidebar);
  const currentUser = useSelector((state) => state.user.currentUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showCreateAlert = canCreateGuardianAlert(currentUser);

  const codeFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normalizeAlertCode(params.get("code") || "");
  }, [location.search]);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const data = await guardianAlertService.getAlertRequests();
        setRequests(data);
      } catch (error) {
        console.error("Erro ao buscar solicitações:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  // Abre o modal quando a navegação traz a solicitação (ex.: Dashboard)
  useEffect(() => {
    const incoming = location.state?.alertRequest;
    if (!incoming?.id) return;

    setRequests((prev) => {
      const exists = prev.some((item) => item.id === incoming.id);
      if (exists) {
        return prev.map((item) =>
          item.id === incoming.id ? { ...item, ...incoming } : item
        );
      }
      return [incoming, ...prev];
    });
    setDetailRequest(incoming);
  }, [location.state]);

  useEffect(() => {
    if (!rejectModalRequest && !feedbackModal && !detailRequest) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (rejectModalRequest && resolvingId !== rejectModalRequest.id) {
        setRejectModalRequest(null);
      } else if (feedbackModal) {
        setFeedbackModal(null);
      } else if (detailRequest) {
        setDetailRequest(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rejectModalRequest, feedbackModal, detailRequest, resolvingId]);

  const handleToggleFullscreen = () => {
    toggleFullscreen();
  };

  const handleCancel = () => {
    navigate("/view_alerts");
  };

  function formatDate(dateString) {
    if (!dateString) return "-";

    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  const getSourceLabel = (source) => {
    if (source === "firms_automated") return "FIRMS Automático";
    if (source === "manual") return "Manual";
    return source || "-";
  };

  const handleResolveRequest = (req, resolution) => {
    if (resolution === "created") {
      if (!showCreateAlert) return;
      dispatch(appStatus("Guardião"));
      navigate("/guardian", {
        state: { activeTab: "new_alert", alertRequest: req },
      });
    }
  };

  /** Abre o alerta vinculado (mesmo fluxo de "Visualizar" na lista de alertas). */
  const handleViewCreatedAlert = async (req) => {
    if (!req?.id || req.resolution !== "created") return;
    if (openingAlertId) return;

    try {
      setOpeningAlertId(req.id);
      await openAlertFromAlertRequest(dispatch, navigate, req);
    } catch (error) {
      console.error("Erro ao abrir alerta da solicitação:", error);
      await Swal.fire({
        title: "Não foi possível abrir o alerta",
        text:
          error?.message ||
          "Não encontramos o alerta vinculado a esta solicitação.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setOpeningAlertId(null);
    }
  };

  const confirmReject = async () => {
    const req = rejectModalRequest;
    if (!req) return;

    try {
      setResolvingId(req.id);
      const updated = await guardianAlertService.resolveAlertRequest(
        req.id,
        "rejected"
      );
      setRequests((prev) =>
        prev.map((item) =>
          item.id === req.id
            ? { ...item, ...updated, resolution: "rejected" }
            : item
        )
      );
      setRejectModalRequest(null);
      setFeedbackModal("success");
    } catch (error) {
      console.error("Erro ao resolver solicitação:", error);
      setRejectModalRequest(null);
      setFeedbackModal("error");
    } finally {
      setResolvingId(null);
    }
  };

  const openRequestModal = (req) => {
    setDetailRequest(req);
  };

  const handleFoundByCode = (alertRequest) => {
    setRequests((prev) => {
      const exists = prev.some((item) => item.id === alertRequest.id);
      if (exists) {
        return prev.map((item) =>
          item.id === alertRequest.id ? { ...item, ...alertRequest } : item
        );
      }
      return [alertRequest, ...prev];
    });
    openRequestModal(alertRequest);
  };

  return (
    <MainContainer collapsed={collapsed}>
      <BackgroundPattern />
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        <PageContent>
          <ButtonRow>
            <ButtonContainerLeft>
              <AreasStyledButton type="button" onClick={handleCancel}>
                Voltar
              </AreasStyledButton>
              <AreasStyledButton
                type="button"
                onClick={handleToggleFullscreen}
              >
                {fullscreenActive ? "Retornar" : "Tela Cheia"}
              </AreasStyledButton>
            </ButtonContainerLeft>
            <ButtonContainerRight />
          </ButtonRow>
          <Header>
            <Title>Solicitações de alerta</Title>
          </Header>

          <AlertRequestCodeSearchBar
            initialCode={codeFromQuery}
            autoSearch={Boolean(codeFromQuery)}
            onFound={handleFoundByCode}
          />

          {loading ? (
            <EmptyMessage>Carregando solicitações...</EmptyMessage>
          ) : requests.length === 0 ? (
            <EmptyMessage>Nenhuma solicitação encontrada.</EmptyMessage>
          ) : (
            <TableContainer>
              <DesktopOnly>
                <Table>
                  <THead>
                    <TR>
                      <TH>Código</TH>
                      <TH>Descrição</TH>
                      <TH>Local</TH>
                      <TH>Data</TH>
                      <TH>Tipo</TH>
                      <TH>Origem</TH>
                      <TH>Status</TH>
                      <TH>Resolução</TH>
                      <TH>Ações</TH>
                    </TR>
                  </THead>

                  <tbody>
                    {requests.map((req) => (
                      <TR key={req.id}>
                        <TD>
                          <StyledButtonSpan onClick={() => openRequestModal(req)}>
                            {req.cod_alerta}
                          </StyledButtonSpan>
                        </TD>
                        <TD>{req.description}</TD>
                        <TD>{req.location}</TD>
                        <TD>{formatDate(req.requested_at)}</TD>
                        <TD>{req.alert_type_detail?.description ?? "—"}</TD>
                        <TD>{getSourceLabel(req.request_source)}</TD>
                        <TD>
                          {req.received ? (
                            <StyledSpan>Recebido</StyledSpan>
                          ) : (
                            <StyledSpan>Pendente</StyledSpan>
                          )}
                        </TD>
                        <TD>
                          <AlertRequestResolutionBadge
                            resolution={req.resolution}
                            opening={openingAlertId === req.id}
                            onOpenAlert={
                              req.resolution === "created"
                                ? () => handleViewCreatedAlert(req)
                                : undefined
                            }
                          />
                        </TD>
                        <TD>
                          <ActionsCell>
                            {showCreateAlert ? (
                              <ActionButton
                                type="button"
                                variant="create"
                                disabled={!!req.resolution}
                                onClick={() =>
                                  handleResolveRequest(req, "created")
                                }
                              >
                                Criar alerta
                              </ActionButton>
                            ) : null}
                            <ActionButton
                              type="button"
                              variant="reject"
                              disabled={
                                !!req.resolution || resolvingId === req.id
                              }
                              onClick={() => setRejectModalRequest(req)}
                            >
                              Rejeitar
                            </ActionButton>
                          </ActionsCell>
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </DesktopOnly>

              <MobileCards>
                {requests.map((req) => (
                  <MobileCard key={`m-${req.id}`}>
                    <MobileCardTitle>
                      <StyledButtonSpan onClick={() => openRequestModal(req)}>
                        {req.cod_alerta || "Solicitação"}
                      </StyledButtonSpan>
                    </MobileCardTitle>
                    <MobileCardMeta>
                      {req.alert_type_detail?.description ?? "—"} ·{" "}
                      {getSourceLabel(req.request_source)}
                    </MobileCardMeta>
                    <MobileCardMeta>{formatDate(req.requested_at)}</MobileCardMeta>
                    <MobileCardMeta>
                      {req.description || "Sem descrição"}
                    </MobileCardMeta>
                    <MobileCardMeta>
                      {req.received ? "Recebido" : "Pendente"} · Local:{" "}
                      {req.location || "—"}
                    </MobileCardMeta>
                    <div>
                      <AlertRequestResolutionBadge
                        resolution={req.resolution}
                        opening={openingAlertId === req.id}
                        onOpenAlert={
                          req.resolution === "created"
                            ? () => handleViewCreatedAlert(req)
                            : undefined
                        }
                      />
                    </div>
                    <MobileCardActions>
                      {showCreateAlert ? (
                        <ActionButton
                          type="button"
                          variant="create"
                          disabled={!!req.resolution}
                          onClick={() => handleResolveRequest(req, "created")}
                        >
                          Criar alerta
                        </ActionButton>
                      ) : null}
                      <ActionButton
                        type="button"
                        variant="reject"
                        disabled={!!req.resolution || resolvingId === req.id}
                        onClick={() => setRejectModalRequest(req)}
                      >
                        Rejeitar
                      </ActionButton>
                      <ActionButton
                        type="button"
                        variant="create"
                        onClick={() => openRequestModal(req)}
                      >
                        Detalhes
                      </ActionButton>
                    </MobileCardActions>
                  </MobileCard>
                ))}
              </MobileCards>
            </TableContainer>
          )}
        </PageContent>
      </SideContainer>

      {detailRequest ? (
        <RequestModal
          req={detailRequest}
          onClose={() => setDetailRequest(null)}
          canCreateAlert={showCreateAlert}
          viewingAlert={openingAlertId === detailRequest.id}
          onViewAlert={async (req) => {
            await handleViewCreatedAlert(req);
          }}
          onCreateAlert={(req) => {
            setDetailRequest(null);
            handleResolveRequest(req, "created");
          }}
          onReject={(req) => {
            setDetailRequest(null);
            setRejectModalRequest(req);
          }}
          onMarkedReceived={(updated) => {
            setRequests((prev) =>
              prev.map((item) =>
                item.id === updated.id ? { ...item, ...updated } : item
              )
            );
          }}
        />
      ) : null}

      {rejectModalRequest && (
        <DialogOverlay>
          <DialogPanel
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-alert-title"
            onClick={(e) => e.stopPropagation()}
          >
            <DialogTitle id="reject-alert-title">
              Rejeitar solicitação
            </DialogTitle>
            <DialogText>
              Confirmar rejeição do escaneamento FIRMS para esta
              solicitação?
              {rejectModalRequest.cod_alerta ? (
                <>
                  {" "}
                  Código:{" "}
                  <strong>{rejectModalRequest.cod_alerta}</strong>.
                </>
              ) : null}
            </DialogText>
            <DialogFooter>
              <DialogButton
                type="button"
                $variant="ghost"
                disabled={resolvingId === rejectModalRequest.id}
                onClick={() => {
                  if (resolvingId !== rejectModalRequest.id) {
                    setRejectModalRequest(null);
                  }
                }}
              >
                Cancelar
              </DialogButton>
              <DialogButton
                type="button"
                $variant="danger"
                disabled={resolvingId === rejectModalRequest.id}
                onClick={confirmReject}
              >
                {resolvingId === rejectModalRequest.id
                  ? "Processando…"
                  : "Confirmar rejeição"}
              </DialogButton>
            </DialogFooter>
          </DialogPanel>
        </DialogOverlay>
      )}

      {feedbackModal && (
        <DialogOverlay>
          <FeedbackPanel
            $variant={feedbackModal}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
            onClick={(e) => e.stopPropagation()}
          >
            <FeedbackIconWrap $variant={feedbackModal}>
              {feedbackModal === "success" ? "✓" : "!"}
            </FeedbackIconWrap>
            <DialogTitle id="feedback-title">
              {feedbackModal === "success"
                ? "Concluído"
                : "Não foi possível concluir"}
            </DialogTitle>
            <DialogText>
              {feedbackModal === "success"
                ? REJECT_SUCCESS_MESSAGE
                : GENERIC_ERROR_MESSAGE}
            </DialogText>
            <DialogFooter>
              <DialogButton
                type="button"
                $variant="primary"
                onClick={() => setFeedbackModal(null)}
              >
                Ok
              </DialogButton>
            </DialogFooter>
          </FeedbackPanel>
        </DialogOverlay>
      )}
    </MainContainer>
  );
}
